import React, { createContext, useContext, useEffect, useState , useMemo } from "react";
import { ResponsiveLine } from "@nivo/line";
import { repeatEffect, settingsConfig } from "utils/commonFunc";
import ApiService from "services/ApiService";
import { defaultChartProps } from "pages/components/CustomDashboardChart";
import { dashboardTheme } from "utils/nivoConfig";

const TrafficTotalContext = createContext(null);
const colors = ["var(--pr-50)", "var(--alert-50)"]; // pr-50: 파랑(월 평균), alert-50: 빨강(금일)

export function SwItsAvgSpeedTotalProvider({ children }) {
  const [currentData, setCurrentData] = useState();

  useEffect(() => {
    return repeatEffect(async () => {
      const response = await ApiService.get(
        "newdashboard/integrated/GetHourSpeed"
      );
      if (response.status === 200) {
        const data = response.data;

        const today = { id: "금일", data: [] };
        const range = { id: "월 평균", data: [] };

        data.rows.forEach((p) => {
          // 수집되지 않은 시간대의 속도는 차트 계산이 가능하도록 0으로 표시합니다.
          today.data.push({ x: p.hour, y: p.todayValue ?? 0 });
          range.data.push({ x: p.hour, y: p.rangeValue ?? 0 });
        });

        setCurrentData({
          chartData: [today, range],
          updateTime: data.updateTime,
          unit: "km/h",
          todayLabel: `평균 속도`,
          todayValue: data.todayAvgSpeed.toFixed(1),
          changeLabel: "월 평균",
          changeValue: `${data.diffPct.toFixed(2)}`,
          rangeValue: data.rangeAvgSpeed.toFixed(1),
        });
      }
    }, settingsConfig().contentUpdateDelaySec);
  }, []);

  return (
    <TrafficTotalContext.Provider value={currentData}>
      {children}
    </TrafficTotalContext.Provider>
  );
}

// export function SwItsAvgSpeedTotalInfo({ showRangeValue = false }) {
//   const currentData = useContext(TrafficTotalContext);
//   if (!currentData) return null;

//   const rawValue = showRangeValue ? currentData.rangeValue : Math.abs(currentData.changeValue);
//   const changeValueClass = showRangeValue
//     ? parseFloat(currentData.rangeValue) >= 0 ? 'plus' : 'minus'
//     : parseFloat(currentData.changeValue) >= 0 ? 'plus' : 'minus';

//   const value = showRangeValue
//     ? rawValue
//     : rawValue;

//   const label = showRangeValue ? '전일 평균' : currentData.changeLabel;

//   return (
//     <div className='traffic-info flx flx_col a_center j_center'>
//       <div className='flx a_flx_end j_center'>
//         <span className='nbr'>
//           {currentData.todayValue}
//         </span>
//         <span className='subTxt'>
//           {currentData.unit}
//         </span>
//       </div>
//       <div className='flx a_center j_center'>
//         <span className='cmpr' style={{ marginRight: '10px' }}>{label}</span>
//         <span
//           className={`cmpr ${changeValueClass}`}
//           style={{ fontWeight: 'var(--ft-Bold)' }}
//         >
//           {parseFloat(currentData.changeValue) >= 0 ? '+ ' : '- '}{value}
//         </span>
//         <span className='cmpr' style={{ marginLeft: '10px' }}>
//           {showRangeValue ? 'km' : '%'}
//         </span>
//       </div>
//     </div>
//   );
// }

export function SwItsAvgSpeedTotalInfo({ showRangeValue = false }) {
  const currentData = useContext(TrafficTotalContext);
  if (!currentData) return null;

  const rawValue = showRangeValue
    ? currentData.rangeValue
    : Math.abs(currentData.changeValue);
  const changeValueClass = showRangeValue
    ? parseFloat(currentData.rangeValue) >= 0
      ? "plus"
      : "minus"
    : parseFloat(currentData.changeValue) >= 0
      ? "plus"
      : "minus";

  const value = showRangeValue ? rawValue : rawValue;

  const label = showRangeValue ? "월 평균" : currentData.changeLabel;

  // 기호는 showRangeValue가 false일 때만 붙임
  const displayValue = showRangeValue
    ? value
    : `${parseFloat(currentData.changeValue) >= 0 ? "+ " : "- "}${value}`;

  // updateTime에서 시간만 받아오기
  const date = new Date(currentData.updateTime);
  let hours = date.getHours() - 1;
  if (hours < 0) hours = 23;
  hours = String(hours).padStart(2, "0");

  return (
    <div className="traffic-info flx flx_col a_center j_center" style={{ marginTop: '-10px' }}>
      <div className="flx a_flx_end j_center">
        <div className="crntTime">
          <div>금일</div>
          <div>{hours}시</div>
        </div>
        <span className="nbr">{currentData.todayValue}</span>
        <span className="subTxt">{currentData.unit}</span>
      </div>
      <div className="flx a_center j_center">
        <span className="cmpr" style={{ marginRight: "10px" }}>
          {label}
        </span>
        <span className="cmpr" style={{ marginRight: "10px" }}>
          {hours}시 속도
        </span>
        <span
          className={`cmpr ${changeValueClass}`}
          style={{ fontWeight: "var(--ft-Bold)" }}
        >
          {displayValue}
        </span>
        <span className="cmpr" style={{ marginLeft: "10px" }}>
          {showRangeValue ? "km/h" : "%"}
        </span>
      </div>
    </div>
  );
}

export function SwItsAvgSpeedTotalChart() {
  const currentData = useContext(TrafficTotalContext) ?? { chartData: [] };
  const allYValues = (currentData.chartData ?? []).flatMap((series) =>
    series.data.map((item) => item.y)
  );

  const maxY = allYValues.length > 0 ? Math.max(...allYValues) : 0;
  const minY = allYValues.length > 0 ? Math.min(...allYValues) : 0;

  const minYRounded = Math.floor(minY / 5) * 5;
  const calculatedMaxY = Math.ceil(maxY / 5) * 5;
  // 모든 값이 같아도 차트 축과 눈금이 유효한 범위를 갖도록 보정합니다.
  const maxYRounded =
    calculatedMaxY === minYRounded ? minYRounded + 5 : calculatedMaxY;

  
  const generateYTicks = () => {
    const step = (maxYRounded - minYRounded) / 4;
    return [minYRounded, minYRounded + step, minYRounded + step * 2, minYRounded + step * 3, maxYRounded];
  };
  
  const reversedData = useMemo(() => {
    if (!currentData?.chartData) return [];
    return [...currentData.chartData].reverse(); 
  }, [currentData?.chartData]);
  if (!currentData.chartData || currentData.chartData.length === 0) return null;
  
  return (
    <div className="traffic-chart" style={{ width: "100%", height: "100%" }}>
      <ResponsiveLine
        {...defaultChartProps}
        theme={{
          ...dashboardTheme,
          grid: {
            line: {
              stroke: "#fff", // 그리드 라인 흰색
              strokeOpacity: 0.2, // 투명도 조절
              strokeWidth: 1,
            },
          },
          axis: {
            ticks: {
              text: {
                fontSize: "var(--ft-26)",
                fontWeight: "var(--ft-Light)",
                fill: "white",
              },
            },
          },
          legends: {
            text: {
              fontSize: "var(--ft-26)",
              fontWeight: "var(--ft-Light)",
            },
          },
        }}
        // data={currentData.chartData.reverse()}
        data={reversedData}
        legends={[
          {
            anchor: "bottom",
            direction: "row",
            translateY: 26,
            itemDirection: "left-to-right",
            itemWidth: 120,
            itemHeight: 0,
            itemTextColor: "#FFFFFF",
            symbolSize: 25,
            fontSize: "var(--ft-26)",
          },
        ]}
        lineWidth={4}
        colors={colors}
        margin={{ top: 10, right: 10, bottom: 50, left: 40 }}
        yScale={{
          type: "linear",
          min: minYRounded,
          max: maxYRounded,
        }}
        axisLeft={{
          // tickValues: Array.from(
          //   { length: (maxYRounded - minYRounded) / 5 + 1 },
          //   (_, i) => minYRounded + i * 5
          // ),
          tickValues: [minYRounded, maxYRounded],
          tickSize: 5,
          tickPadding: 5,
          tickRotation: 0,
          tickColor: "#fff",
          legendPosition: "middle",
          format: (value) => `${value}`,
        }}
        axisBottom={null} // X축은 사용하지 않음
        enableGridX={false}
        enableGridY={true}
        gridYValues={generateYTicks()}
        enableArea={true}
        areaBaselineValue={minYRounded}
        areaOpacity={0.1}
        enablePoints={false}
        isInteractive={true}
        useMesh={true}
        tooltip={({ point }) => (
          <div
            style={{
              background: "#333",
              padding: "12px",
              color: "white",
              fontSize: "14px",
              borderRadius: "4px",
            }}
          >
            <div>시간: {point.data.x}시</div>
            <div>
              평균 속도:{" "}
              {point.data.y.toLocaleString(undefined, {
                minimumFractionDigits: 1,
                maximumFractionDigits: 1,
              })}{" "}
              km/h
            </div>
          </div>
        )}
      />
    </div>
  );
}
